import React from "react";
import { LocateFixed, MapPin } from "lucide-react";

const Required = () => (
  <span className="tsp-required" aria-hidden="true">
    {" "}
    *
  </span>
);

// The inputs of the "Add City Visit" form, driven by useCityVisitForm. Rendered
// in a card on the City Visits screen and in the journey page's "Visit Now"
// dialog; each container supplies its own submit button.
//
// `locked` ("Visit Now"): the journey and the stop are fixed.
export default function CityVisitFormFields({ form, journeys, journeyId, cities, citiesLoading, locked = false }) {
  const { draft, change, changeJourney, error, saving, locating, capture, captureLocation, range, coordinates, selectedCity } = form;

  return (
    <>
      <div className="tsp-form-row">
        <div className="tsp-form-field">
          <label htmlFor="cv-journey">
            Journey
            <Required />
          </label>
          <select
            id="cv-journey"
            aria-required="true"
            className="tsp-select tsp-select-block"
            value={journeyId}
            disabled={saving || locked}
            onChange={(event) => changeJourney(event.target.value)}
          >
            <option value="">Select Journey</option>
            {journeys.map((journey) => (
              <option key={journey.id} value={journey.id}>
                {journey.name}
              </option>
            ))}
          </select>
        </div>
        <div className="tsp-form-field">
          <label htmlFor="cv-city">
            City
            <Required />
          </label>
          <select id="cv-city" aria-required="true" className="tsp-select tsp-select-block" value={draft.locationId} disabled={saving || locked || !journeyId || citiesLoading} onChange={change("locationId")}>
            <option value="">{citiesLoading ? "Loading cities…" : "Select City"}</option>
            {cities.map((city) => (
              <option key={city.id} value={city.id} disabled={Boolean(city.status)}>
                {city.status ? `${city.label} (${city.status})` : city.label}
              </option>
            ))}
          </select>
        </div>
        <div className="tsp-form-field">
          <label htmlFor="cv-date">
            Date
            <Required />
          </label>
          <input id="cv-date" aria-required="true" type="date" className="tsp-input" value={draft.date} disabled={saving} min={range.startDate || undefined} max={range.endDate || undefined} onChange={change("date")} />
        </div>
      </div>
      {selectedCity && (
        <dl className="tsp-visit-place" data-testid="cv-place">
          <div>
            <dt>State</dt>
            <dd>{selectedCity.stateName || "—"}</dd>
          </div>
          <div>
            <dt>District</dt>
            <dd>{selectedCity.districtName || "—"}</dd>
          </div>
          <div>
            <dt>Taluka / Town / Municipality</dt>
            <dd>{selectedCity.cityName || "—"}</dd>
          </div>
          <div>
            <dt>Planned Areas</dt>
            <dd>{selectedCity.areas && selectedCity.areas.length ? selectedCity.areas.join(", ") : "—"}</dd>
          </div>
        </dl>
      )}
      <div className="tsp-form-row" style={{ marginTop: 14 }}>
        <div className="tsp-form-field">
          <label htmlFor="cv-time">
            Time
            <Required />
          </label>
          <input id="cv-time" aria-required="true" type="time" className="tsp-input" value={draft.time} disabled={saving} onChange={change("time")} />
        </div>
        <div className="tsp-form-field">
          <label htmlFor="cv-areas">
            Areas Visited
          </label>
          <input id="cv-areas" type="text" className="tsp-input" placeholder="Comma-separated, e.g. Fort, Market" value={draft.areasVisited} disabled={saving} onChange={change("areasVisited")} />
        </div>
        <div className="tsp-form-field tsp-visit-location-field">
          <div className="tsp-visit-location-head">
            <span className="tsp-visit-location-label">
              Visit Location <span className="tsp-required" aria-hidden="true">*</span>
            </span>
            <button type="button" className="tsp-btn tsp-btn-outline-primary tsp-btn-sm tsp-visit-locate-btn" onClick={captureLocation} disabled={saving || locating}>
              <LocateFixed size={12} className={locating ? "tsp-spin" : ""} aria-hidden="true" />
              {locating ? "Locating…" : "Use my location"}
            </button>
          </div>
          <div className="tsp-visit-coordinates">
            <label htmlFor="cv-latitude" className="tsp-sr-only">
              Latitude
            </label>
            <input
              id="cv-latitude"
              type="text"
              inputMode="decimal"
              className="tsp-input"
              placeholder="Latitude (e.g. 19.2960)"
              aria-required="true"
              value={draft.latitude}
              disabled={saving}
              onChange={change("latitude")}
            />
            <label htmlFor="cv-longitude" className="tsp-sr-only">
              Longitude
            </label>
            <input
              id="cv-longitude"
              type="text"
              inputMode="decimal"
              className="tsp-input"
              placeholder="Longitude (e.g. 73.0580)"
              aria-required="true"
              value={draft.longitude}
              disabled={saving}
              onChange={change("longitude")}
            />
          </div>
          {!coordinates.error && (
            <span className="tsp-visit-coordinates-note" data-testid="cv-coordinates-note">
              <MapPin size={11} aria-hidden="true" /> Will be saved as {coordinates.latitude}, {coordinates.longitude}
              {capture ? ` — from this device${capture.accuracy !== null ? `, accurate to about ${capture.accuracy} m` : ""}` : " — entered by hand"}
            </span>
          )}
        </div>
      </div>
      <div className="tsp-form-field" style={{ marginTop: 14 }}>
        <label htmlFor="cv-remarks">Remarks</label>
        <input id="cv-remarks" type="text" className="tsp-input" value={draft.remarks} disabled={saving} onChange={change("remarks")} />
      </div>
      {error && (
        <p className="tsp-form-error" role="alert">
          {error}
        </p>
      )}
    </>
  );
}
